import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { withToastManager } from "react-toast-notifications";
import BrandLinksRow from "../../Helper/BrandLinksRow";
import Helper from "../../Helper/helper";
import { fetchCreatorBySlug } from "../../Helper/creators";
import ToastDemo from "../../Helper/toaster";
import EmptyState from "../../Dashboard/EmptyState";
import ExperienceGate from "../../Experience/ExperienceGate";
import OrganizationActionBar from "../../Organization/Public/OrganizationActionBar";
import RichTextContent from "../../Helper/RichTextContent";
import EntityIdentity from "../../Helper/EntityIdentity";
import ShareMenu from "../../Helper/ShareMenu";
import { AppearanceButton } from "../../Appearance";
import {
  resolveMediaUrl,
  resolveMediaBackground,
  handleMediaImgError,
} from "../../Helper/mediaUrl";
import ActivityFeedSection from "../../Community/ActivityFeedSection";
import CommunityFollowButton from "../../Community/CommunityFollowButton";
import { fetchCommunityFollows } from "../../Community/communitySocialApi";
import {
  fetchShopProducts,
  formatShopPrice,
} from "../../Shop/shopApi";
import { creatorPublicPath } from "../../Helper/entityRoutes";
import {
  normalizeSocialNetworkKey,
  socialNetworkMeta,
  extractCustomIconUrl,
} from "../../Helper/socialNetworks";
import { shouldRenderEntityStorefrontSection, filterEntityPublicShopProducts } from "../../Organization/Public/entityStorefrontVisibility";
import EntityPublicVideoLibrary from "../../Organization/Public/EntityPublicVideoLibrary";
import "../../Organization/Public/OrganizationPublicPage.css";
import "../../Community/CommunitySocial.css";

/**
 * Public Creator page — canonical /creator/:slug
 * Consumes Organization public shell + shared EntityIdentity / Brand Links / AE.
 * Legacy /creators/:slug permanently redirects (Phase D.4.1).
 */
class CreatorPublicPage extends Helper {
  state = {
    loading: true,
    creator: null,
    error: false,
    isFollowing: false,
    products: [],
    productsLoading: false,
    aboutExpanded: false,
    videoDetailsFirst: null,
    redirect: false,
    redirectPPV: false,
    redirectPaymentOption: false,
    redirectSubscription: false,
    playButtonClicked: false,
  };

  componentDidMount() {
    this.setState({ playButtonClicked: false });
    this.loadCreator();
  }

  componentDidUpdate(prevProps) {
    const prevSlug =
      prevProps.match && prevProps.match.params
        ? prevProps.match.params.slug
        : "";
    const nextSlug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    if (prevSlug !== nextSlug) {
      this.setState({ playButtonClicked: false });
      this.loadCreator();
    }
  }

  loadCreator = async () => {
    const slug =
      this.props.match && this.props.match.params
        ? this.props.match.params.slug
        : "";
    this.setState({
      loading: true,
      error: false,
      isFollowing: false,
      products: [],
    });
    const creator = await fetchCreatorBySlug(slug);
    let isFollowing = !!(creator && creator.is_following);
    if (creator && creator.id && !isFollowing) {
      const hasSession =
        localStorage.getItem("userId") && localStorage.getItem("accessToken");
      if (hasSession) {
        const follows = await fetchCommunityFollows("creator");
        if (!follows.error) {
          isFollowing = (follows.follows || []).some(
            (row) => Number(row.followable_id) === Number(creator.id)
          );
        }
      }
    }
    this.setState({
      loading: false,
      creator,
      error: !creator,
      isFollowing,
    });
    if (creator && creator.id) {
      this.loadProducts(creator);
    }
  };

  loadProducts = async (creator) => {
    this.setState({ productsLoading: true });
    // Shared entity shop scope: organization_id OR creator_id (same API + visibility gate).
    const params = { per_page: 24 };
    if (creator.organization_id) {
      params.organization_id = creator.organization_id;
    } else if (creator.creator_id || creator.id) {
      params.creator_id = creator.creator_id || creator.id;
    }
    const result = await fetchShopProducts(params);
    const scoped =
      params.organization_id || params.creator_id
        ? result && result.products
          ? result.products
          : []
        : [];
    this.setState({
      products: scoped,
      productsLoading: false,
    });
  };

  handlePlayVideo = async (event, adminVideoId) => {
    event.preventDefault();

    const inputData = {
      admin_video_id: adminVideoId,
      skip: 0,
    };

    var details = await this.onlySingleVideoFirst(inputData);

    if (!details) {
      ToastDemo(
        this.props.toastManager,
        (this.state.videoDetailsError &&
          this.state.videoDetailsError.error_messages) ||
          "Unable to start playback",
        "error"
      );
      return;
    }

    this.redirectStatus(details);
    this.setState({ playButtonClicked: true });
  };

  getBrandLinks(creator) {
    if (!creator) {
      return [];
    }

    const fromPayload =
      creator.brand_links && Array.isArray(creator.brand_links.links)
        ? creator.brand_links.links
        : [];

    const filtered = fromPayload.filter(
      (link) =>
        link &&
        link.url &&
        link.is_enabled !== false &&
        link.is_visible !== false
    );
    if (filtered.length) {
      return filtered;
    }

    const social = creator.social_links || {};
    if (!social || typeof social !== "object") {
      return [];
    }

    // Array form from API social_links
    if (Array.isArray(social)) {
      return [];
    }

    const built = [];
    let order = 10;
    Object.keys(social).forEach((key) => {
      const network = normalizeSocialNetworkKey(key);
      const value = social[key];
      const url = String(
        value && typeof value === "object" ? value.url || "" : value || ""
      ).trim();
      if (!url) {
        return;
      }
      const meta = socialNetworkMeta(network);
      const iconUrl =
        value && typeof value === "object" ? extractCustomIconUrl(value) : null;
      const item = {
        id: "creator-social-client-" + (network || "link") + "-" + order,
        display_name:
          (value && value.display_name) || meta.label || network,
        url,
        builtin_icon:
          (value && value.builtin_icon) ||
          meta.builtin_icon ||
          "fas fa-link",
        display_order: order,
        open_in_new_tab: true,
        is_enabled: true,
        is_visible: true,
        css_class: meta.css_class || network,
      };
      if (iconUrl) {
        item.icon_type = "custom";
        item.icon_url = iconUrl;
      }
      built.push(item);
      order += 10;
    });
    return built;
  }

  renderLogo(creator) {
    const src = creator.logo_path || creator.avatar;
    if (src) {
      return (
        <img
          src={resolveMediaUrl(src)}
          alt={creator.display_name || creator.name || "Creator"}
          className="org-logo"
          onError={handleMediaImgError}
          loading="lazy"
        />
      );
    }

    return (
      <div
        className="org-logo-placeholder"
        aria-hidden="true"
        title={creator.display_name || creator.name || "Creator"}
      >
        <i className="fas fa-user" />
      </div>
    );
  }

  renderVideoCarousel(videos, videoSubcategories) {
    return (
      <EntityPublicVideoLibrary
        videos={videos}
        videoSubcategories={videoSubcategories}
        onPlayVideo={this.handlePlayVideo}
        keyPrefix="creator"
      />
    );
  }

  renderStorefront(products) {
    const visibleProducts = filterEntityPublicShopProducts(products);
    if (!visibleProducts.length) {
      return null;
    }

    return (
      <div className="org-storefront-grid">
        {visibleProducts.map((product) => {
          const priceLabel = product.is_free
            ? "Free"
            : formatShopPrice(product.effective_price);
          return (
            <Link
              key={product.id}
              className="org-storefront-card"
              to={`/shop/product/${encodeURIComponent(product.slug)}`}
            >
              <div className="org-storefront-card__media">
                {product.primary_image ? (
                  <img
                    src={product.primary_image}
                    alt={product.name || ""}
                    loading="lazy"
                    onError={handleMediaImgError}
                  />
                ) : (
                  <div className="org-storefront-card__placeholder" />
                )}
              </div>
              <div className="org-storefront-card__body">
                <strong>{product.name}</strong>
                {priceLabel ? <span>{priceLabel}</span> : null}
              </div>
            </Link>
          );
        })}
      </div>
    );
  }

  renderAbout(creator) {
    const html = creator.description || creator.biography || "";
    const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    const PREVIEW_LEN = 280;
    const needsCollapse = plain.length > PREVIEW_LEN;
    const expanded = this.state.aboutExpanded;
    const displayName = creator.display_name || creator.name || "this creator";

    let previewText = plain;
    if (needsCollapse && !expanded) {
      let cut = plain.slice(0, PREVIEW_LEN);
      const lastSpace = cut.lastIndexOf(" ");
      if (lastSpace > Math.floor(PREVIEW_LEN * 0.55)) {
        cut = cut.slice(0, lastSpace);
      }
      previewText = cut.trimEnd() + "…";
    }

    return (
      <section
        className="org-about-section"
        aria-labelledby="creator-about-heading"
      >
        <h2 id="creator-about-heading" className="org-videos-heading">
          About
        </h2>
        <div className="org-about-body">
          {needsCollapse && !expanded ? (
            <p className="org-description">{previewText}</p>
          ) : html ? (
            <RichTextContent html={html} className="org-description rich-text" />
          ) : (
            <p className="org-empty-inline">Welcome to {displayName}.</p>
          )}
        </div>
        {needsCollapse ? (
          <AppearanceButton
            variant="readmore"
            type="button"
            className="org-about-toggle"
            onClick={() =>
              this.setState({ aboutExpanded: !this.state.aboutExpanded })
            }
          >
            {expanded ? "Read Less" : "Read More"}
          </AppearanceButton>
        ) : null}
      </section>
    );
  }

  render() {
    const {
      loading,
      creator,
      error,
      playButtonClicked,
      products,
      productsLoading,
    } = this.state;

    if (playButtonClicked) {
      const returnToVideo = this.renderRedirectPage(
        this.state.videoDetailsFirst
      );
      if (returnToVideo != null) {
        return returnToVideo;
      }
    }

    if (loading) {
      return (
        <div className="main-sec-content" style={{ padding: "3rem 1rem" }}>
          <EmptyState
            title="Loading…"
            message="Loading creator profile."
            statusRole="status"
          />
        </div>
      );
    }

    if (error || !creator) {
      return (
        <div className="main-sec-content" style={{ padding: "3rem 1rem" }}>
          <EmptyState
            title="Creator not found"
            message="This creator is unavailable or not public."
          >
            <Link to="/home" className="ae-btn ae-btn--primary">
              Browse Home
            </Link>
          </EmptyState>
        </div>
      );
    }

    const experience =
      creator.experience || { type: "none", should_present: false };
    const coverPath = creator.cover_image_path || creator.banner;
    const hasCover = Boolean(coverPath);
    const brandLinks = this.getBrandLinks(creator);
    const coverStyle = hasCover
      ? { backgroundImage: resolveMediaBackground(coverPath) }
      : undefined;
    const displayName = creator.display_name || creator.name || "Creator";
    const typeLabel =
      creator.type_label || creator.creator_type_label || "Creator";
    const canonicalPath =
      creator.public_path || creatorPublicPath(creator.slug || creator.id);
    const canonicalHref =
      (typeof window !== "undefined" ? window.location.origin : "") +
      canonicalPath;

    return (
      <ExperienceGate destinationType="creator" presentation={experience}>
        <Helmet>
          <title>{displayName}</title>
          <link rel="canonical" href={canonicalHref} />
        </Helmet>
        <div className="organization-public-page creator-public-page">
          {hasCover ? (
            <div
              className="org-hero org-hero--cover"
              style={coverStyle}
              role="img"
              aria-label={displayName + " cover"}
            />
          ) : (
            <div className="org-hero org-hero--plain" />
          )}

          <div className="org-page-shell">
            <header className="org-header org-header--identity">
              <div className="org-header-inner">
                {this.renderLogo(creator)}
                <EntityIdentity
                  name={displayName}
                  handle={creator.slug}
                  typeLabel={typeLabel}
                  entity={creator}
                />
              </div>

              <div className="org-actions-block">
                <div
                  className="org-action-bar org-action-bar--primary"
                  role="group"
                  aria-label="Creator actions"
                >
                  <CommunityFollowButton
                    type="creator"
                    id={creator.id}
                    initiallyFollowing={
                      this.state.isFollowing || !!creator.is_following
                    }
                    followerCount={
                      typeof creator.follower_count === "number"
                        ? creator.follower_count
                        : null
                    }
                    showCount
                    className="ae-btn ae-btn--secondary ae-btn--entity-follow org-action-btn org-follow-btn"
                    onChange={(detail) => {
                      this.setState({
                        isFollowing: !!(detail && detail.following),
                        creator: {
                          ...creator,
                          is_following: !!(detail && detail.following),
                          follower_count:
                            detail && typeof detail.follower_count === "number"
                              ? detail.follower_count
                              : creator.follower_count,
                        },
                      });
                    }}
                  />
                  {creator.donation_mode === "external" &&
                  creator.donation_url ? (
                    <AppearanceButton
                      href={creator.donation_url}
                      variant="donate"
                      className="org-action-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Donate
                    </AppearanceButton>
                  ) : (
                    <AppearanceButton
                      to={`/creator/${creator.slug || creator.id}/donate`}
                      variant="donate"
                      className="org-action-btn"
                    >
                      Donate
                    </AppearanceButton>
                  )}
                  <OrganizationActionBar
                    organizationName={displayName}
                    website={creator.website}
                    donationUrl={null}
                    extraActions={[]}
                  />
                  <ShareMenu
                    title={displayName}
                    text={`Check out ${displayName}`}
                    className="org-action-btn"
                    variant="share"
                  />
                </div>
                {brandLinks.length ? (
                  <BrandLinksRow
                    links={brandLinks}
                    className="social-share org-brand-links"
                  />
                ) : null}
              </div>
            </header>

            <div className="org-content-layout org-content-layout--home">
              <div className="org-content-main">
                <ActivityFeedSection
                  contextType="creator"
                  creatorId={creator.id}
                  title="Activity"
                  headingId="creator-activity-heading"
                  className="org-activity-feed"
                />
              </div>

              <aside
                className="org-content-sidebar"
                aria-label="Creator about, media, and storefront"
              >
                {this.renderAbout(creator)}

                <section
                  className="org-videos-section"
                  aria-labelledby="creator-videos-heading"
                >
                  <h2 id="creator-videos-heading" className="org-videos-heading">
                    Latest Videos
                  </h2>
                  {this.renderVideoCarousel(
                    creator.videos,
                    creator.video_subcategories
                  )}
                </section>

                {shouldRenderEntityStorefrontSection({
                  products,
                  productsLoading,
                }) ? (
                  <section
                    className="org-storefront-section"
                    aria-labelledby="creator-storefront-heading"
                  >
                    <h2
                      id="creator-storefront-heading"
                      className="org-videos-heading"
                    >
                      Storefront
                    </h2>
                    {this.renderStorefront(products)}
                  </section>
                ) : null}
              </aside>
            </div>
          </div>
        </div>
      </ExperienceGate>
    );
  }
}

export default withToastManager(CreatorPublicPage);
